/**
 * Числа для интерфейса: разряды через неразрывный пробел, десятичная запятая (как в RU и KK),
 * компактные значения для плиток метрик и размеры файлов.
 */
import type { AppLocale } from './locale-data';
import { NBSP, groupDigits } from './money';

const MINUS = '−';

export interface NumberFormatOptions {
  /** Максимум знаков после запятой (по умолчанию 0). */
  maxFractionDigits?: number;
  /** Минимум знаков после запятой (по умолчанию 0). */
  minFractionDigits?: number;
}

/** 1234567.891 → «1 234 567,89» (при maxFractionDigits = 2). */
export function formatNumber(value: number, options: NumberFormatOptions = {}): string {
  if (!Number.isFinite(value)) return '—';
  const max = options.maxFractionDigits ?? 0;
  const min = Math.min(options.minFractionDigits ?? 0, max);
  const negative = value < 0;
  const fixed = Math.abs(value).toFixed(max);
  const [whole = '0', rawFraction = ''] = fixed.split('.');
  let fraction = rawFraction.replace(/0+$/, '');
  if (fraction.length < min) fraction = fraction.padEnd(min, '0');
  const text = groupDigits(whole) + (fraction ? `,${fraction}` : '');
  return (negative && Number(fixed) !== 0 ? MINUS : '') + text;
}

/** Доля 0…1 → «62%»; с дробной частью при необходимости: 0.6234 → «62,3%» (maxFractionDigits = 1). */
export function formatPercent(ratio: number, options: NumberFormatOptions = {}): string {
  return `${formatNumber(ratio * 100, options)}%`;
}

const COMPACT_SUFFIXES: Record<AppLocale, readonly string[]> = {
  ru: ['', 'тыс.', 'млн', 'млрд'],
  kk: ['', 'мың', 'млн', 'млрд'],
};

/** Компактное число для плиток метрик: 12900 → «12,9 тыс.» / «12,9 мың», 4200000 → «4,2 млн». */
export function formatCompactNumber(value: number, locale: AppLocale): string {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs < 10_000) return formatNumber(value, { maxFractionDigits: abs < 10 ? 1 : 0 });
  let tier = Math.min(Math.floor(Math.log10(abs) / 3), 3);
  let scaled = value / 10 ** (tier * 3);
  if (Math.abs(Number(scaled.toFixed(1))) >= 1000 && tier < 3) {
    tier += 1;
    scaled = value / 10 ** (tier * 3);
  }
  const digits = Math.abs(scaled) >= 100 ? 0 : 1;
  return `${formatNumber(scaled, { maxFractionDigits: digits })}${NBSP}${COMPACT_SUFFIXES[locale][tier]}`;
}

const FILE_UNITS: Record<AppLocale, readonly string[]> = {
  ru: ['Б', 'КБ', 'МБ', 'ГБ'],
  kk: ['Б', 'КБ', 'МБ', 'ГБ'],
};

/** Размер файла: 1536 → «1,5 КБ». */
export function formatFileSize(bytes: number, locale: AppLocale): string {
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < 3) {
    value /= 1024;
    unit += 1;
  }
  return `${formatNumber(value, { maxFractionDigits: unit === 0 ? 0 : 1 })}${NBSP}${FILE_UNITS[locale][unit]}`;
}

/** Длительность в секундах → «05:07» или «1:02:07» (таймер записи, плеер). */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(rest).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}
